<template>
  <aside class="sidebar shadow">
    <div class="sidebar-header">
      <router-link
        to="/dashboard"
        class="brand"
      >
        <div class="logo-container">
          <font-awesome-icon
            icon="calendar-check"
            class="logo-icon"
          />
        </div>
        <span class="brand-name">OpenCal</span>
      </router-link>
    </div>

    <nav class="sidebar-nav">
      <router-link
        to="/dashboard"
        class="nav-item"
        active-class="active"
      >
        <font-awesome-icon
          icon="calendar-days"
          class="nav-icon"
        />
        <span class="nav-label">{{ $t('header.nav.dashboard') }}</span>
      </router-link>

      <router-link
        to="/dashboard/event_types"
        class="nav-item"
        active-class="active"
      >
        <font-awesome-icon
          icon="list-check"
          class="nav-icon"
        />
        <span class="nav-label">{{ $t('header.nav.event_types') }}</span>
      </router-link>

      <div
        v-if="user?.roles.includes('ROLE_ADMIN')"
        class="nav-divider"
      >
        <span>{{ $t('header.nav.admin') }}</span>
      </div>

      <router-link
        v-if="user?.roles.includes('ROLE_ADMIN')"
        to="/dashboard/admin"
        class="nav-item"
        active-class="active"
      >
        <font-awesome-icon
          icon="users-gear"
          class="nav-icon"
        />
        <span class="nav-label">{{ $t('header.nav.admin') }}</span>
      </router-link>
    </nav>

    <div class="sidebar-footer">
      <div class="sidebar-credits">
        <div class="text-center small">
          <a
            href="https://git.var-lab.com/opencal/opencal"
            target="_blank"
            class="credits-link"
          >
            OpenCal {{ OPENCAL_VERSION }}
          </a>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {User} from "../../types/User";
import {OPENCAL_VERSION} from "../../env";

defineProps<{ user: User | null }>();
</script>

<style scoped lang="scss">
.sidebar {
  width: 260px;
  height: 100vh;
  background: #1e1e2d;
  color: #a2a3b7;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1050;
  transition: all 0.3s ease;
}

.sidebar-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #fff;

  .logo-container {
    width: 35px;
    height: 35px;
    background: linear-gradient(135deg, #4c6ef5 0%, #364fc7 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.75rem;

    .logo-icon {
      color: #fff;
      font-size: 1.2rem;
    }
  }

  .brand-name {
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
}

.sidebar-nav {
  flex: 1;
  padding: 1.5rem 0.75rem;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #a2a3b7;
  text-decoration: none;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &.active {
    background: rgba(76, 110, 245, 0.1);
    color: #4c6ef5;

    .nav-icon {
      color: #4c6ef5;
    }
  }

  .nav-icon {
    width: 20px;
    margin-right: 0.75rem;
    font-size: 1.1rem;
  }

  .nav-label {
    font-size: 0.95rem;
    font-weight: 500;
  }
}

.nav-divider {
  padding: 1.5rem 1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #494b66;
  letter-spacing: 1px;
}

.sidebar-footer {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.user-info {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 35px;
  height: 35px;
  background: #323248;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: #4c6ef5;
  font-weight: 700;
  font-size: 0.9rem;
  border: 1.5px solid rgba(76, 110, 245, 0.2);
}

.user-details {
  flex: 1;
}

.user-email {
  font-size: 0.85rem;
  font-weight: 500;
}

.sidebar-credits {
  border-top-color: rgba(255, 255, 255, 0.05) !important;
}

.credits-link {
  color: #a2a3b7;
  text-decoration: none;
  transition: color 0.2s ease;
  font-weight: 500;

  &:hover {
    color: #fff;
  }
}

.credits-sub {
  color: #494b66;
  font-size: 0.7rem;
}
</style>
